import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { toMaterialIcon } from '../../../shared/icon-map';
import { getNavHelp } from '../../../shared/nav-help';
import { ThemeService } from '../../../services/theme.service';

export interface VwNavNode {
  name: string;
  url?: string;
  icon?: string;
  children?: VwNavNode[];
  open?: boolean;
}

@Component({
  selector: 'app-vw-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnDestroy {
  toMaterialIcon = toMaterialIcon;
  getNavHelp = getNavHelp;

  private _navItems: VwNavNode[] = [];
  @Input() set navItems(items: VwNavNode[]) {
    this._navItems = items || [];
    this.autoExpandActive();
  }
  get navItems(): VwNavNode[] {
    return this._navItems;
  }

  @Input() collapsed = false;

  @Output() help = new EventEmitter<void>();

  flyoutPosition = { top: 0, left: 0 };

  sidebarLogo: string = 'assets/Icon-196.png';

  private routerSub: Subscription;
  private themeSub: Subscription;

  constructor(private router: Router, private themeService: ThemeService) {
    this.routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.autoExpandActive();
      }
    });

    this.themeSub = this.themeService.theme$.subscribe(theme => {
      this.updateSidebarLogo(theme);
    });

    this.updateSidebarLogo(this.themeService.current);
  }

  isActive(url: string): boolean {
    return !!url && this.router.url.indexOf(url) === 0;
  }

  hasActiveChild(item: VwNavNode): boolean {
    if (!item.children) {
      return false;
    }
    return item.children.some(child =>
      this.isActive(child.url) || (!!child.children && child.children.some(grandchild => this.isActive(grandchild.url)))
    );
  }

  toggleGroup(item: VwNavNode, event: MouseEvent): void {
    if (this.collapsed) {
      return;
    }
    item.open = !item.open;
  }

  closeGroup(item: VwNavNode): void {
    item.open = false;
  }

  private closeTimer: any;

  onGroupEnter(item: VwNavNode, event: MouseEvent): void {
    if (!this.collapsed || !item.children || !item.children.length) {
      return;
    }
    clearTimeout(this.closeTimer);
    this._navItems.forEach(i => { if (i !== item) { i.open = false; } });
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.flyoutPosition = { top: rect.top, left: rect.right + 8 };
    item.open = true;
  }

  onGroupLeave(item: VwNavNode): void {
    if (!this.collapsed) {
      return;
    }
    this.closeTimer = setTimeout(() => { item.open = false; }, 250);
  }

  onFlyoutEnter(): void {
    clearTimeout(this.closeTimer);
  }

  onFlyoutLeave(item: VwNavNode): void {
    this.closeTimer = setTimeout(() => { item.open = false; }, 150);
  }

  private updateSidebarLogo(theme: string): void {
    this.sidebarLogo = theme === 'dark' ? 'assets/VOWEL12.png' : 'assets/Icon-196.png';
  }

  private autoExpandActive(): void {
    if (this.collapsed) {
      this._navItems.forEach(item => item.open = false);
      return;
    }
    this._navItems.forEach(item => {
      if (this.hasActiveChild(item)) {
        item.open = true;
      }
      if (item.children) {
        item.children.forEach(child => {
          if (child.children && child.children.some(grandchild => this.isActive(grandchild.url))) {
            child.open = true;
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
    if (this.themeSub) {
      this.themeSub.unsubscribe();
    }
    clearTimeout(this.closeTimer);
  }
}
